import { useCallback} from 'react';
import { useDropzone } from 'react-dropzone';
import { Emoji } from '../../../../../../__gqltypes__';

export type EmojiSource = {
  file: File;
  emoji: Emoji;
  preview: string;
  ext: string;
}

const utfCodeRegex = /^([A-Fa-f0-9]{4})$/;

export function validateUtfCode(utf: string): boolean {
  // we need to check if utf code is in PUA [E000, F8FF]
  const res = utfCodeRegex.exec(utf);
  if (!res || !res[1]) {
    return false;
  }
  const utfCodeNum = parseInt(res[1], 16);
  if (utfCodeNum > 0xF8FF || utfCodeNum < 0xE000) {
    return false;
  }
  return true;
}

function extractNameAndUtf(filename: string): { name: string; utfCode: string; ext: string; }  {
  if (!filename.includes('.') || !filename.includes('_')) {
    throw new Error(`Wrong format of filename, missing extension or "_" separator. Filename: ${filename}`);
  }
  const [nameWithoutExt, ext] = filename.split('.');
  const [name, utfCode] = nameWithoutExt.split('_');
  
  if (!validateUtfCode(utfCode)) {
    throw new Error(`Incorrect value of utf code. Code has to be in range: [E000, F8FF]. Filename: ${filename}`);
  }

  return {
    name, 
    utfCode,
    ext,
  };
}

type Props = {
  idBuilder: (utfCode: string) => string;
  onEmojiSources: (sources: EmojiSource[]) => void;
}

export default function EmojiFactory({idBuilder,onEmojiSources}: Props): JSX.Element {
    const getEmojisFromFiles = useCallback((files: File[]) => {
        const emojis: Array<EmojiSource> = files.map((file, index) => {
          const {name, utfCode, ext} = extractNameAndUtf(file.name);
          return {
            file,
            emoji: {
              __typename: 'Emoji',
              id: idBuilder(utfCode),
              name,
              utfCode,
              asset: '',
              cost: {
                __typename: 'CurrencyDiamond',
                amount: 0,
                id: 'CurrencyDiamon',
              }
            },
            ext,
            preview: URL.createObjectURL(file),
          };
        });
        onEmojiSources(emojis);
      }, [idBuilder, onEmojiSources]);
    
    const onDrop = useCallback((acceptedFiles: File[]) => {
      const utfCodeRegex_ = /([A-Fa-f0-9]{4})/;
      const sortedFiles = acceptedFiles.sort((a, b) => {
        const codeA = utfCodeRegex_.exec(a.name);
        const codeB = utfCodeRegex_.exec(b.name);
        if (codeA && codeB) {
          const valA = codeA[1]; 
          const valB = codeB[1];
          return valA < valB ? -1 : valA > valB ? 1 : 0;
        }
        return 0;
      });
      getEmojisFromFiles(sortedFiles);
    }, [getEmojisFromFiles]); 

    const { getRootProps, getInputProps, isDragActive} = useDropzone({ onDrop, accept: '.png' });
    return (
      <div 
        {...getRootProps()} 
        style={isDragActive ? {
            border: '2px solid white',
            backgroundColor: '#4e73df',
            color: 'white',
            borderRadius: '10px',
          } : {
            border: '2px solid #4e73df', borderRadius: '10px'}} 
            className="text-center"
        >
        <p>Emoji Factory</p>
        <input {...getInputProps()} />
        {isDragActive ? <p>Drop Files Here...</p> : <p>Drop files or click here to upload</p>}
      </div>
    );
}